'use client';

import { useState } from 'react';
import { formatCurrency } from '@/utils/format';
import { Calculator, Lightbulb } from 'lucide-react';

const APR_MARKERS = [0, 5, 10, 15, 20, 25, 30];
const PAYMENT_MARKERS = [0, 30, 60, 90, 120];

const APR_MIN = APR_MARKERS[0];
const APR_MAX = APR_MARKERS[APR_MARKERS.length - 1];
const DAYS_MIN = PAYMENT_MARKERS[0];
const DAYS_MAX = PAYMENT_MARKERS[PAYMENT_MARKERS.length - 1];

const SLIDER_THUMB_BASE_DIAMETER_PX = 24;

const getTimingMarkerLabel = (value: number) => (value === 0 ? 'On Time' : `${value}d`);

const getSliderMarkerStyle = (value: number, min: number, max: number) => {
  const clampedValue = Math.min(Math.max(value, min), max);
  const safeRange = Math.max(max - min, 1);
  const ratio = Math.min(1, Math.max(0, (clampedValue - min) / safeRange));
  const preciseRatio = Number.isFinite(ratio) ? (ratio * 100).toFixed(2) : '0';
  return {
    left: `${preciseRatio}%`
  };
};

export function APRCalculator() {
  const [balanceInput, setBalanceInput] = useState<string>('1000');
  const [apr, setApr] = useState<number>(18);
  const [aprInput, setAprInput] = useState<string>('18');
  const [daysLate, setDaysLate] = useState<number>(30);

  const parsedBalance = parseFloat(balanceInput);
  const balance = Number.isFinite(parsedBalance) && parsedBalance >= 0 ? parsedBalance : 0;

  const calculateInterest = (days: number) => {
    if (days === 0) return 0;
    const dailyRate = apr / 100 / 365;
    return balance * dailyRate * days;
  };

  const calculateLateFee = (days: number) => {
    if (days === 0) return 0;
    if (days <= 30) return 25;
    if (days <= 60) return 35;
    if (days <= 90) return 45;
    return 55; // 120+ days
  };

  const handleBalanceInputChange = (value: string) => {
    const stripped = value.replace(/,/g, '');
    if (stripped === '' || /^\d*(\.\d*)?$/.test(stripped)) {
      setBalanceInput(stripped);
    }
  };

  const handleAPRInputChange = (value: string) => {
    // Allow clearing the field completely
    if (value.trim() === '') {
      setAprInput('');
      setApr(0);
      return;
    }

    // Only allow valid decimal numbers
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setAprInput(value);

    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
      return;
    }

    const clamped = Math.max(APR_MIN, Math.min(APR_MAX, parsed));
    const rounded = Math.round(clamped * 100) / 100;
    setApr(rounded);
  };

  const handleAPRChange = (value: number) => {
    const newValue = Math.max(APR_MIN, Math.min(APR_MAX, Math.round(value)));
    setApr(newValue);
    setAprInput(newValue.toString());
  };

  const getDaysLateLabel = (days: number) => {
    if (days === 0) return 'On Time';
    return `${days} Days Late`;
  };

const getDaysLateColor = (days: number) => {
  if (days === 0) return 'text-green-600';
  if (days <= 30) return 'text-orange-500';
  if (days <= 60) return 'text-orange-600';
  if (days <= 90) return 'text-red-500';
  return 'text-red-600';
};

const getAddedCostTone = (value: number) => {
  if (value <= 0) return 'text-emerald-600';
  if (value < 50) return 'text-rose-400';
  if (value < 150) return 'text-rose-500';
  if (value < 300) return 'text-rose-600';
  return 'text-rose-700';
};

  const interest = calculateInterest(daysLate);
  const lateFee = calculateLateFee(daysLate);
  const totalExtraCost = interest + lateFee;
  const totalAmount = balance + totalExtraCost;
  const percentageIncrease = balance > 0 ? (totalExtraCost / balance) * 100 : 0;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-white via-blue-50/40 to-indigo-100/50 p-8 shadow-[0_24px_60px_-28px_rgba(37,99,235,0.45)]">
      <div className="pointer-events-none absolute inset-y-0 right-[-25%] h-[140%] w-2/3 rotate-12 bg-gradient-to-l from-indigo-400/15 via-transparent to-transparent blur-3xl" aria-hidden="true"></div>
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4 shadow-lg">
            <Calculator className="h-7 w-7 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">APR Impact Calculator</h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto">
            See the real cost of carrying a balance and late payments
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">

        {/* Left Side - Inputs and Controls */}
        <div className="flex flex-col gap-6 h-full">

          {/* APR Slider */}
          <div className="rounded-2xl border border-slate-200/60 bg-white/95 backdrop-blur-sm p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Annual percentage rate
                </p>
                <p className="mt-1 text-xs text-gray-500">Fine-tune your card&rsquo;s APR to see interest impact</p>
              </div>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  min={APR_MIN}
                  max={APR_MAX}
                  step={0.01}
                  value={aprInput}
                  placeholder="0"
                  onChange={(e) => handleAPRInputChange(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="h-12 w-[4.75rem] rounded-xl border border-blue-200/70 bg-white/90 pl-3 pr-2 text-left text-lg font-semibold text-blue-600 shadow-sm focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200/60 focus:shadow-md transition-all duration-200 tabular-nums placeholder:text-blue-300 [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <span className="text-blue-500 font-semibold text-sm">%</span>
                </div>
              </div>
            </div>
            <div className="relative mb-8">
              <input
                type="range"
                min={APR_MIN}
                max={APR_MAX}
                step={1}
                value={Math.round(apr)}
                onChange={(e) => handleAPRChange(parseFloat(e.target.value))}
                className="ios-slider w-full h-2.5 rounded-full bg-slate-200/80 appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              />
              <div
                className="pointer-events-none absolute inset-x-0 top-full mt-3 px-3"
              >
                <div className="relative h-4">
                  {APR_MARKERS.map((marker) => {
                    const isActive = Math.abs(marker - apr) <= 0.5;
                    const markerStyle = getSliderMarkerStyle(marker, APR_MIN, APR_MAX);
                    const markerClasses = isActive
                      ? 'text-blue-600 font-semibold'
                      : 'text-gray-500';
                    return (
                      <span
                        key={marker}
                        className={`absolute text-xs font-medium transition-colors duration-200 ${markerClasses}`}
                        style={{ ...markerStyle, transform: 'translateX(-50%)' }}
                      >
                        {marker}%
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Days Late Slider */}
          <div className="rounded-2xl border border-emerald-100/60 bg-white/95 backdrop-blur-sm p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-semibold text-slate-800">
                  Payment timing
                </p>
                <p className="mt-1 text-xs text-gray-500">Model how delays add fees and interest</p>
              </div>
              <span
                className={`inline-flex h-10 items-center rounded-xl bg-gray-50 px-3 text-sm font-semibold ${getDaysLateColor(daysLate)}`}
              >
                {getDaysLateLabel(daysLate)}
              </span>
            </div>
            <div className="relative mb-8">
              <input
                type="range"
                min={DAYS_MIN}
                max={DAYS_MAX}
                step={30}
                value={daysLate}
                onChange={(e) => setDaysLate(parseInt(e.target.value))}
                className="ios-slider ios-slider--timing w-full h-2.5 rounded-full bg-slate-200/80 appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
              />
              <div
                className="pointer-events-none absolute inset-x-0 top-full mt-3 px-3"
              >
                <div className="relative h-4">
                  {PAYMENT_MARKERS.map((marker) => {
                    const markerStyle = getSliderMarkerStyle(marker, DAYS_MIN, DAYS_MAX);
                    const isActive = marker === daysLate;
                    const markerClasses = isActive
                      ? 'text-emerald-600 font-semibold'
                      : 'text-gray-500';
                    return (
                      <span
                        key={marker}
                        className={`absolute text-xs font-medium transition-colors duration-200 ${markerClasses}`}
                        style={{
                          ...markerStyle,
                          transform: 'translateX(-50%)'
                        }}
                     >
                        {getTimingMarkerLabel(marker)}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Combined Summary */}
        <div className="flex flex-col gap-6 h-full">
          {/* Main Summary Card */}
          <div className="rounded-2xl border border-slate-200/60 bg-white/95 backdrop-blur-sm p-6 shadow-lg">
            {/* Header with Total and Badge */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">Estimated Balance</p>
                <p className="text-3xl font-bold text-slate-900 tracking-tight">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
              <span
                className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${
                  daysLate > 0 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                }`}
              >
                {daysLate > 0 ? `+${percentageIncrease.toFixed(1)}%` : 'On time'}
              </span>
            </div>

            {/* Clean Two-Column Layout */}
            <div className="grid grid-cols-2 gap-6">
              {/* Original Balance */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Original balance</p>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 text-sm">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={balanceInput === '0' ? '' : balanceInput}
                    placeholder="0"
                    onChange={(e) => handleBalanceInputChange(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="w-full rounded-xl border border-slate-200/70 bg-slate-50/80 px-3 py-3 pl-8 text-lg font-semibold text-slate-900 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200/60 focus:shadow-sm transition-all duration-200 placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Added Cost */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-600">Added cost</p>
                <div className="flex items-center h-[42px]">
                  <p className={`text-lg font-semibold tabular-nums ${
                    daysLate > 0 ? getAddedCostTone(totalExtraCost) : 'text-emerald-600'
                  }`}>
                    {daysLate > 0 ? `+${formatCurrency(totalExtraCost)}` : '$0.00'}
                  </p>
                </div>
              </div>
            </div>

            {/* Optional on-time message */}
            {daysLate === 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500 text-center">
                  Paying on time keeps your total equal to the original balance
                </p>
              </div>
            )}
          </div>

          {/* Cost Breakdown */}
          <div className="rounded-2xl border border-slate-200/60 bg-white/95 backdrop-blur-sm p-6 shadow-lg lg:flex-1">
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-800">Cost breakdown</h4>
            </div>

            {daysLate > 0 ? (
              <div className="space-y-3">
                {interest > 0 && (
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50/80 border border-slate-100">
                    <span className="text-sm text-slate-600">Interest ({daysLate}d)</span>
                    <span className="text-sm font-semibold text-slate-900">{formatCurrency(interest)}</span>
                  </div>
                )}
                {lateFee > 0 && (
                  <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-slate-50/80 border border-slate-100">
                    <span className="text-sm text-slate-600">Late fee</span>
                    <span className="text-sm font-semibold text-slate-900">{formatCurrency(lateFee)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between py-3 px-4 rounded-xl border border-red-200/80 bg-red-50/90 shadow-sm">
                  <span className="text-sm font-semibold text-red-700">Total extra cost</span>
                  <span className="text-sm font-bold text-red-700">{formatCurrency(totalExtraCost)}</span>
                </div>
              </div>
            ) : (
              <div className="py-4 px-4 rounded-xl bg-emerald-50/90 border border-emerald-200/80 shadow-sm">
                <p className="text-sm font-medium text-emerald-700 text-center">
                  No extra costs when paying on time
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Smart Tips */}
        <div className="mt-8 rounded-2xl border border-blue-200/60 bg-gradient-to-r from-blue-50/80 via-white/95 to-indigo-50/75 p-6 backdrop-blur-sm shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
              <Lightbulb className="h-5 w-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-3">Smart Payment Tips</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-3">
                  <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                  <span>Pay your full balance before the due date to avoid all interest charges.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-500"></span>
                  <span>Target higher APR cards first so interest costs do not snowball.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ios-slider {
          background: linear-gradient(90deg,
            rgba(59, 130, 246, 0.18) 0%,
            rgba(59, 130, 246, 0.32) 40%,
            rgba(99, 102, 241, 0.4) 100%);
          border-radius: 9999px;
          position: relative;
        }

        .ios-slider--timing {
          background: linear-gradient(90deg,
            rgba(16, 185, 129, 0.18) 0%,
            rgba(245, 158, 11, 0.28) 50%,
            rgba(239, 68, 68, 0.33) 100%);
        }

        .ios-slider::-webkit-slider-thumb {
          appearance: none;
          height: ${SLIDER_THUMB_BASE_DIAMETER_PX}px;
          width: ${SLIDER_THUMB_BASE_DIAMETER_PX}px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.2);
          border: 2px solid rgba(148, 163, 184, 0.35);
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .ios-slider::-webkit-slider-thumb:hover {
          transform: scale(1.08);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.28);
        }

        .ios-slider::-moz-range-thumb {
          height: ${SLIDER_THUMB_BASE_DIAMETER_PX}px;
          width: ${SLIDER_THUMB_BASE_DIAMETER_PX}px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.2);
          border: 2px solid rgba(148, 163, 184, 0.35);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
